import { FC, useEffect, useMemo } from 'react';

import { getDateFromString, TODAY_START_OF_DAY } from 'utils/date';

import { useBreakpoint } from '@hh.ru/magritte-ui';

import { useMultiCreationDateValidation } from 'hooks/multiCreation/useMultiCreationDateValidation';
import useTrls from 'hooks/translation/useTrls';

import { DateSelect } from 'components/common/DateSelect/DateSelect';

import { selectMultiCreationCampaignsCreating, setMultiCreationStartAt } from 'store/models/multiCreation';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    datePlaceholder: 'MultiCreationAuction.BidsAndBudgetsTable.dates.placeholder',
    startDatePlaceholder: 'MultiCreationAuction.BidsAndBudgetsTable.dates.startDatePlaceholder',
};

export const AuctionDateStart: FC<{ vacancyId: number }> = ({ vacancyId }) => {
    const currentAuctionsToCreate = useSelector(({ multiCreation }) => multiCreation.auctionsToCreate[vacancyId]);
    const { startAt, endAt, startAtValidationError } = currentAuctionsToCreate;

    const isCampaignsCreating = useSelector(selectMultiCreationCampaignsCreating);
    const { isMobile } = useBreakpoint();
    const dispatch = useDispatch();
    const trls = useTrls();
    const { validateStartDate } = useMultiCreationDateValidation(vacancyId);

    const selectedDate = useMemo(() => getDateFromString(startAt), [startAt]);

    const minDate = TODAY_START_OF_DAY;
    const maxDate = endAt ? getDateFromString(endAt) : undefined;

    useEffect(() => {
        validateStartDate(startAt);
    }, [startAt, validateStartDate]);

    const handleDateSelect = (date: string) => {
        dispatch(setMultiCreationStartAt({ vacancyId, value: date }));

        validateStartDate(date);
    };

    return (
        <DateSelect
            data-qa="auction-date-start-input"
            maxDate={maxDate}
            minDate={minDate}
            onDateSelect={handleDateSelect}
            placeholder={trls(TrlKeys[isMobile ? 'startDatePlaceholder' : 'datePlaceholder'])}
            selectedDate={selectedDate || undefined}
            inputDisabled={isCampaignsCreating}
            inputInvalid={!!startAtValidationError}
            errorMessage={startAtValidationError || undefined}
            elevatePlaceholder={isMobile}
            inputSize={isMobile ? 'large' : 'medium'}
        />
    );
};
